import { ImageWrapper, IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import { Box, Typography, Grid, Slide } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SCREEN_RESOLVER } from '../../routeHandling/RoutesNomenclature';

const styles: IStyles = {
  cardImage: {
    width: '100%',
    height: { xs: pxToRem(200), md: pxTovW(350), lg: pxTovW(250) },
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow:
        'rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      zIndex: 1,
    },
  },
};

export const QRResolver = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get QRId from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const qrId = searchParams.get('QRId');

  const teacher_app_url =
    process.env.TEACHER_APP_URL ||
    (window.location.origin.includes('4201')
      ? window.location.origin.replace('4201', '4200')
      : window.location.origin.replace('learn', 'teach'));

  // State to track which cards should be visible
  const [checkedStates, setCheckedStates] = useState([true, true]); // [teacher, student]

  // ------------------------------------------------------------

  const handleCardAnimationState = (cardIndex: number) => {
    setCheckedStates((prevState) => {
      return prevState.map((checked, idx) =>
        idx === cardIndex ? checked : !checked
      );
    });
  };

  // ------------------------------------------------------------

  const teacherCardClickHandler = () => {
    console.log('teacherCardClickHandler:');

    if (!qrId) {
      return;
    }

    // Trigger slide animation
    handleCardAnimationState(0);
    // Wait for animation before navigating

    let teacherUrl = `${teacher_app_url}${SCREEN_RESOLVER}?QRId=${qrId}`;

    setTimeout(() => {
      window.location.href = teacherUrl;
    }, 500);
  };

  // ------------------------------------------------------------

  const studentCardClickHandler = () => {
    console.log('studentCardClickHandler:');

    if (!qrId) {
      return;
    }

    // Trigger slide animation
    handleCardAnimationState(1);
    // Wait for animation before navigating
    setTimeout(() => {
      navigate(SCREEN_RESOLVER + `?QRId=${qrId}`);
    }, 500);
  };

  // ------------------------------------------------------------

  return (
    <Box
      sx={{
        marginX: 'auto',
        marginY: { xs: pxToRem(20), md: pxTovW(20) },
        paddingX: { xs: pxToRem(5), md: pxTovW(50), lg: pxTovW(340) },
      }}
    >
      <Typography
        variant="h2"
        sx={{
          marginBottom: { xs: pxToRem(10), md: pxTovW(40) },
          textAlign: 'center',
          fontWeight: 600,
        }}
      >
        Please Choose the User
      </Typography>

      {!qrId && (
        <Box
          sx={{
            padding: { xs: pxToRem(10), md: pxTovW(10) },
            backgroundColor: 'error.light',
            borderRadius: 1,
            marginBottom: 3,
          }}
        >
          <Typography color="error">
            No QRId found in the URL. Please use a valid link.
          </Typography>
        </Box>
      )}

      <Grid container spacing={{ xs: pxToRem(50), md: pxTovW(50) }}>
        {/* Teacher Card */}
        <Grid item xs={12} md={6}>
          <Slide in={checkedStates[0]} timeout={500}>
            <Box>
              <UserCard
                title="Teacher"
                backgroundImage="teacher_toon"
                onCardClick={teacherCardClickHandler}
              />
            </Box>
          </Slide>
        </Grid>

        {/* Student Card */}
        <Grid item xs={12} md={6}>
          <Slide in={checkedStates[1]} timeout={500}>
            <Box>
              <UserCard
                title="Student"
                backgroundImage="student_toon"
                onCardClick={studentCardClickHandler}
              />
            </Box>
          </Slide>
        </Grid>
      </Grid>
    </Box>
  );
};

// =========================================================

interface UserCardProps {
  title: string;
  backgroundImage: string;
  onCardClick: () => void;
}

const UserCard = ({ title, backgroundImage, onCardClick }: UserCardProps) => {
  return (
    <Box onClick={() => onCardClick()} sx={{ cursor: 'pointer' }}>
      <ImageWrapper
        name={backgroundImage}
        type="png"
        parentFolder="images"
        styles={styles.cardImage}
      />

      <Box
        sx={{
          paddingY: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(10) },
          border: '1px solid black',
        }}
      >
        <Typography variant="h3" textAlign="center">
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

// =========================================================
